import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Check, AlertCircle } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';

const inputCls = 'w-full bg-white/5 border border-white/10 text-white h-11 px-3 placeholder:text-white/20 focus:border-primary/60 transition-all duration-200';
const labelCls = 'text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block';
const selectCls = 'w-full bg-white/5 border border-white/10 text-white h-11 px-3 focus:border-primary/60 transition-all duration-200';

const TITLES = {
  points: 'Adjust Loyalty Points',
  free_booking: 'Comped Booking',
  discount_code: 'Issue Discount Code',
};

export default function CompensationModal({ actionType, customerEmail, customerName, onClose }) {
  const queryClient = useQueryClient();
  const [experiences, setExperiences] = useState([]);
  const [rigs, setRigs] = useState([]);
  const [form, setForm] = useState({ reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    base44.entities.BookingExperience.list().then((e) => setExperiences((e || []).filter((x) => x.active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))));
    base44.entities.BookingRig.list().then((r) => setRigs(r || []));
  }, []);

  useEffect(() => {
    if (actionType === 'points') setForm({ reason: '', points: '' });
    else if (actionType === 'free_booking') setForm({ reason: '', experience_type: '', rig_type: '', date: '', time_slot: '10:00', duration: 1, customer_phone: '', notes: '' });
    else if (actionType === 'discount_code') setForm({ reason: '', discount_value: '' });
    setError('');
    setResult(null);
  }, [actionType]);

  const submit = async () => {
    if (!form.reason?.trim()) { setError('A reason is required for audit.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('adminCompensateCustomer', {
        action_type: actionType,
        customer_email: customerEmail,
        customer_name: customerName,
        ...form,
        points: form.points ? Number(form.points) : undefined,
        discount_value: form.discount_value ? Number(form.discount_value) : undefined,
        duration: form.duration ? Number(form.duration) : undefined,
      });
      if (!res.success) { setError(res.error || 'Something went wrong.'); return; }
      setResult(res);
      queryClient.invalidateQueries({ queryKey: ['crm-loyalty', customerEmail] });
      queryClient.invalidateQueries({ queryKey: ['crm-consumer-account', customerEmail] });
    } catch (e) {
      setError(e?.message || 'Request failed.');
    } finally {
      setSaving(false);
    }
  };

  const timeSlots = ['09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00'];

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] bg-black/80 flex items-start justify-center overflow-y-auto p-4 pt-[5vh]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-[#0d0d0f] border border-white/10 w-full max-w-lg my-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest">COMPENSATION</p>
              <h2 className="text-white font-black text-lg leading-tight mt-0.5">{TITLES[actionType]}</h2>
            </div>
            <button onClick={onClose} className="text-white/30 hover:text-white transition-colors p-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          {result ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 border border-green-500/30 bg-green-500/10 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <p className="text-white font-bold text-sm mb-2">Compensation applied</p>
              <p className="text-white/50 text-xs mb-1">{result.details}</p>
              {result.reference && <p className="text-white/30 text-[10px] font-mono">Ref: {result.reference}</p>}
              <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-primary text-white text-xs font-bold tracking-wide hover:bg-primary/90">
                DONE
              </button>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <div className="bg-white/5 border border-white/5 px-3 py-2 text-xs text-white/50">
                <span className="text-white/30">Customer:</span> {customerName || customerEmail}
              </div>

              {/* Reason — shared */}
              <div>
                <label className={labelCls}>REASON / JUSTIFICATION *</label>
                <textarea
                  className={inputCls + ' h-16 py-2 resize-none'}
                  placeholder="e.g. Apology for equipment failure during session"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                />
              </div>

              {actionType === 'points' && (
                <div>
                  <label className={labelCls}>POINTS (use minus for deduction)</label>
                  <input
                    type="number"
                    className={inputCls}
                    placeholder="e.g. 500 or -200"
                    value={form.points}
                    onChange={(e) => setForm({ ...form, points: e.target.value })}
                  />
                </div>
              )}

              {actionType === 'free_booking' && (
                <>
                  <div>
                    <label className={labelCls}>EXPERIENCE</label>
                    <select className={selectCls} value={form.experience_type} onChange={(e) => setForm({ ...form, experience_type: e.target.value })}>
                      <option value="">Select…</option>
                      {experiences.map((exp) => <option key={exp.key} value={exp.key}>{exp.name} (£{exp.price})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>RIG</label>
                    <select className={selectCls} value={form.rig_type} onChange={(e) => setForm({ ...form, rig_type: e.target.value })}>
                      <option value="child_sim">Auto-assign</option>
                      {rigs.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>DATE</label>
                      <input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                    </div>
                    <div>
                      <label className={labelCls}>TIME</label>
                      <select className={selectCls} value={form.time_slot} onChange={(e) => setForm({ ...form, time_slot: e.target.value })}>
                        {timeSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>CUSTOMER PHONE (optional)</label>
                    <input className={inputCls} placeholder="07…" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>NOTES (optional)</label>
                    <textarea className={inputCls + ' h-12 py-2 resize-none'} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </>
              )}

              {actionType === 'discount_code' && (
                <div>
                  <label className={labelCls}>DISCOUNT VALUE (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className={inputCls}
                    placeholder="e.g. 25"
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  />
                  <p className="text-white/30 text-[10px] mt-1.5">A one-time fixed-amount code will be generated automatically.</p>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-xs border border-red-500/20 bg-red-500/5 px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 py-2.5 border border-white/10 text-white/50 text-xs font-bold tracking-wide hover:bg-white/5">
                  CANCEL
                </button>
                <button onClick={submit} disabled={saving} className="flex-1 py-2.5 bg-primary text-white text-xs font-bold tracking-wide hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {saving ? 'APPLYING…' : 'APPLY'}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}